import React, { useEffect, useState } from "react";
import * as Slider from "@radix-ui/react-slider";
import {
  ShieldCheck,
  Check,
  Wallet,
  Ticket,
  TestTube,
  Pill,
  Video,
  Building2,
  Loader2,
} from "lucide-react";
import {
  CUSTOM_BENEFIT_CATALOG,
  buildCustomBenefitSelection,
} from "../utils/customBenefitCatalog";
import useBucketPricing from "../hooks/useBucketPricing";

// Selections settle for this long (no slide/click) before their tier ids are
// sent to the pricing API. A slider drag or a burst of tier-button clicks
// fires many intermediate selections; only the one the user actually lands
// on needs a price request.
const PRICE_FETCH_DEBOUNCE_MS = 300;

const TINT = {
  teal: { bg: "#E6F6F4", fg: "#0F9488" },
  blue: { bg: "#E8F1FE", fg: "#2563EB" },
  violet: { bg: "#F1EBFC", fg: "#6D28D9" },
  amber: { bg: "#FDF3E3", fg: "#B45309" },
  rose: { bg: "#FCEAEF", fg: "#BE123C" },
  green: { bg: "#E9F7EE", fg: "#15803D" },
  slate: { bg: "#F1F1F4", fg: "#475569" },
  pink: { bg: "#FCEAFE", fg: "#A21CAF" },
};

const hexToRgba = (hex, alpha = 1) => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CATEGORY_TINT = {
  diagnostic: TINT.violet,
  pharmacy: TINT.green,
  "tele-consultation": TINT.rose,
  "in-clinic-consultation": TINT.amber,
};

const CATEGORY_ICONS = {
  diagnostic: TestTube,
  pharmacy: Pill,
  "tele-consultation": Video,
  "in-clinic-consultation": Building2,
};

const CustomBenefitPanel = ({ open, onClose, onContinue, tenantId }) => {
  const [selections, setSelections] = useState({});
  const { getPrice, requestPrice } = useBucketPricing(tenantId);

  // Whenever the settled selection changes, ask for a price on every
  // currently-active tier id. requestPrice is itself memoized per benefit id
  // (see useBucketPricing), so re-selecting a tier the user already picked
  // before - toggle off/on, slide back to a previous value, etc. - never
  // re-triggers a network call; only genuinely new ids do.
  useEffect(() => {
    const timer = setTimeout(() => {
      Object.entries(selections).forEach(([key, tierIndex]) => {
        const category = CUSTOM_BENEFIT_CATALOG.find((c) => c.key === key);
        const tierId = category?.tiers[tierIndex]?.id;
        if (tierId != null) requestPrice(tierId);
      });
    }, PRICE_FETCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [selections, requestPrice]);

  if (!open) return null;

  const toggleIncluded = (key) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (next[key] != null) {
        delete next[key];
      } else {
        next[key] = 0;
      }
      return next;
    });
  };

  const setTier = (key, tierIndex) => {
    setSelections((prev) => ({ ...prev, [key]: tierIndex }));
  };

  const resolvePrice = (benefitId) => getPrice(benefitId)?.price ?? 0;
  const selectedBenefits = buildCustomBenefitSelection(selections, resolvePrice);

  // A tier is only "ready" once its price has actually come back - keeps the
  // Continue CTA from carrying a stale/zero price while a request is still
  // in flight or failed.
  const pendingSelections = Object.entries(selections).filter(([key, tierIndex]) => {
    const category = CUSTOM_BENEFIT_CATALOG.find((c) => c.key === key);
    const tierId = category?.tiers[tierIndex]?.id;
    return tierId != null && getPrice(tierId)?.status !== "success";
  });
  const pricesReady = pendingSelections.length === 0;

  const totalPrice = selectedBenefits.reduce(
    (sum, b) => sum + (b.price || 0),
    0
  );

  // Credits (tele/in-clinic consultation) don't carry a ₹ price, so they're
  // bifurcated from the amount total instead of folded into it. "unlimited"
  // is a sentinel, not a number — one unlimited tier selected makes the
  // whole credits total "Unlimited" rather than a sum.
  const creditBenefits = selectedBenefits.filter(
    (b) => b.customType === "credit"
  );
  const hasUnlimitedCredit = creditBenefits.some(
    (b) => b.creditAmount === "unlimited"
  );
  const totalCredits = hasUnlimitedCredit
    ? "Unlimited"
    : creditBenefits.reduce((sum, b) => sum + (Number(b.creditAmount) || 0), 0);

  return (
    <div
      id="custom-benefit-panel"
      className="mt-6 rounded-2xl border border-white/60 bg-gradient-to-br from-[#F3EEFB] via-[#F7F2FA] to-[#EFF6F5] p-4 animate-card-in sm:p-6"
      aria-labelledby="custom-benefit-panel-title"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h3
            id="custom-benefit-panel-title"
            className="text-2xl font-extrabold leading-tight text-purple-800 sm:text-3xl"
          >
            Custom Benefit
          </h3>
          <p className="mt-1 text-sm text-[#5B5866] sm:text-base">
            Set how much cover you want, by category.
          </p>
        </div>
      </div>

      {/* Category cards */}
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {CUSTOM_BENEFIT_CATALOG.map((category) => {
          const tierIndex = selections[category.key];
          const included = tierIndex != null;
          const activeTier = category.tiers[included ? tierIndex : 0];
          const activeTierPricing = getPrice(activeTier.id);
          const FallbackIcon = CATEGORY_ICONS[category.key] || ShieldCheck;
          const tint = CATEGORY_TINT[category.key] || TINT.slate;

          return (
            <div
              key={category.key}
              className="group relative min-w-0 overflow-hidden rounded-2xl border bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md sm:p-5"
              style={{
                borderColor: included ? hexToRgba(tint.fg, 0.35) : "#EEECF3",
                boxShadow: included
                  ? "0 6px 20px rgba(20,19,26,0.08)"
                  : "0 1px 2px rgba(20,19,26,0.03)",
              }}
            >
              {/* Decorative tint wash */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-0 w-2/3"
                style={{
                  background: `radial-gradient(circle at 85% 65%, ${hexToRgba(
                    tint.bg,
                    0.9
                  )}, transparent 70%)`,
                }}
              />

              {/* Decorative illustration — only shown from sm up, and behind everything, so it never competes with text or controls for space */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute bottom-0 right-0 hidden h-20 w-20 bg-cover bg-no-repeat opacity-80 sm:block sm:h-24 sm:w-32 lg:w-36"
                style={{
                  backgroundImage: `url(${category.bgImage})`,
                  backgroundPosition: "bottom right",
                }}
              />

              <div className="relative z-10 flex flex-col gap-3">
                {/* Toggle zone: checkbox is its own column on the left at every screen size;
                    the icon sits directly in front of the category name in one row to its right. */}
                <label className="flex w-full cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={included}
                    onChange={() => toggleIncluded(category.key)}
                    className="peer sr-only"
                    aria-label={`Include ${category.name}`}
                  />

                  {/* Checkbox column */}
                  <span
                    aria-hidden="true"
                    className="mt-2.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-slate-400 sm:mt-3.5"
                    style={{
                      backgroundColor: included ? tint.fg : "#FFFFFF",
                      borderColor: included ? tint.fg : hexToRgba(tint.fg, 0.35),
                    }}
                  >
                    {included && (
                      <Check size={13} strokeWidth={4} className="text-white" />
                    )}
                  </span>

                  {/* Content column: icon + name row, description below */}
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2.5">
                      <span
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:h-14 sm:w-14"
                        style={{ backgroundColor: tint.bg }}
                      >
                        {category.icon ? (
                          <img
                            src={category.icon}
                            alt=""
                            className="h-8 w-8 object-contain sm:h-[42px] sm:w-[42px]"
                          />
                        ) : (
                          <FallbackIcon size={20} color={tint.fg} strokeWidth={2} />
                        )}
                      </span>

                      <span className="flex min-w-0 flex-wrap items-center gap-2">
                        <span
                          className="min-w-0 break-normal text-lg font-extrabold leading-tight sm:text-[20px]"
                          style={{ color: tint.fg, overflowWrap: "normal", wordBreak: "normal" }}
                        >
                          {category.name}
                        </span>

                        {included && (
                          <span
                            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold"
                            style={{
                              backgroundColor: tint.fg,
                              color: "#FFFFFF",
                            }}
                          >
                            {activeTierPricing?.status === "success" ? (
                              `₹${activeTierPricing.price.toLocaleString("en-IN")}`
                            ) : activeTierPricing?.status === "error" ? (
                              "Price unavailable"
                            ) : (
                              <>
                                <Loader2 size={11} className="animate-spin" />
                                Fetching…
                              </>
                            )}
                          </span>
                        )}

                        {included && category.type === "credit" && (
                          <span
                            className="shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold"
                            style={{
                              backgroundColor: hexToRgba(tint.fg, 0.12),
                              color: tint.fg,
                            }}
                          >
                            {activeTier.label}
                          </span>
                        )}
                      </span>
                    </span>

                    <span className="mt-1.5 block text-sm text-[#5B5866]">
                      {category.description}
                    </span>
                  </span>
                </label>

                {/* Slider */}
                <div
                  className={`w-full sm:max-w-[75%] ${included ? "" : "opacity-50"}`}
                >
                  <Slider.Root
                    className={`relative flex w-full touch-none select-none items-center py-2 ${
                      included ? "" : "pointer-events-none"
                    }`}
                    min={0}
                    max={category.tiers.length - 1}
                    step={1}
                    value={[included ? tierIndex : 0]}
                    disabled={!included}
                    onValueChange={([val]) => setTier(category.key, val)}
                    aria-label={`${category.name} amount`}
                  >
                    <Slider.Track
                      className="relative h-2.5 w-full grow overflow-hidden rounded-full"
                      style={{ backgroundColor: tint.bg }}
                    >
                      <Slider.Range
                        className="absolute h-full rounded-full"
                        style={{ backgroundColor: tint.fg }}
                      />
                    </Slider.Track>

                    <Slider.Thumb
                      className={`block h-3 w-3 rounded-full bg-white transition-transform duration-150 focus:outline-none focus-visible:scale-110 ${
                        included
                          ? "cursor-grab hover:scale-110 active:scale-95 active:cursor-grabbing"
                          : ""
                      }`}
                      style={{
                        boxShadow: `0 2px 6px rgba(20,19,26,0.25), 0 0 0 3px ${tint.fg}`,
                      }}
                    />
                  </Slider.Root>
                </div>

                {/* Tier chips */}
                <div className="flex flex-wrap gap-2">
                  {category.tiers.map((tier, idx) => {
                    const isActive = included && idx === tierIndex;
                    return (
                      <button
                        key={tier.id}
                        type="button"
                        onClick={() => setTier(category.key, idx)}
                        disabled={!included}
                        className="whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-40 sm:px-3 sm:text-[13px]"
                        style={{
                          backgroundColor: isActive ? tint.fg : "#FFFFFF",
                          color: isActive ? "#FFFFFF" : tint.fg,
                          border: `1px solid ${
                            isActive ? tint.fg : hexToRgba(tint.fg, 0.35)
                          }`,
                        }}
                      >
                        {tier.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: wallet + trust signal + CTA */}
      <div className="mt-6 flex flex-col gap-5 border-t border-white/60 pt-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-4 sm:gap-5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EDE4FB]">
              <Wallet size={16} className="text-[#6D28D9]" />
            </span>
            <span>
              <span className="block text-xs font-semibold text-[#8A8798]">
                Total amount
              </span>
              <span className="block text-lg font-extrabold text-[#14131A]">
                ₹{totalPrice.toLocaleString("en-IN")}
              </span>
            </span>
          </div>

         

          <span
            className="hidden h-10 w-px bg-[#E4E1EC] sm:block"
            aria-hidden="true"
          />

          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#DCFCE7]">
              <ShieldCheck size={16} className="text-[#15803D]" />
            </span>
            <span>
              <span className="block text-sm font-bold text-[#14131A]">
                Secure & safe payments
              </span>
              <span className="block text-xs text-[#8A8798]">
                Your payment information is always protected
              </span>
            </span>
          </div>
        </div>

        <div className="flex flex-col items-start gap-1.5 sm:items-end">
          <button
            type="button"
            onClick={() => onContinue(selectedBenefits)}
            disabled={selectedBenefits.length === 0 || !pricesReady}
            className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#4C1D95] px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#3B0764] active:scale-[0.99] disabled:pointer-events-none disabled:opacity-40 sm:w-auto"
          >
            Continue

            <svg
              aria-hidden="true"
              fill="none"
              height="20"
              viewBox="0 0 24 24"
              width="20"
              xmlns="http://www.w3.org/2000/svg"
              className="transition-transform duration-200 ease-out group-hover:translate-x-1"
            >
              <path
                d="m13 4.58569 7.4142 7.41421-7.4142 7.4142-1.4142-1.4142 5-5h-12.5858v-2h12.5858l-5-4.99999z"
                fill="currentColor"
              />
            </svg>
          </button>
          {selectedBenefits.length === 0 && (
            <p className="text-xs font-medium text-[#8A8798]">
              Select at least one benefit to continue
            </p>
          )}
          {selectedBenefits.length > 0 && !pricesReady && (
            <p className="text-xs font-medium text-[#8A8798]">
              Fetching latest pricing…
            </p>
          )}
        </div>
      </div>

      <style>{`
        @keyframes card-fade-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-card-in { animation: card-fade-in 220ms ease-out; }
      `}</style>
    </div>
  );
};

export default CustomBenefitPanel;